import React from 'react';
import { useTranslation } from 'react-i18next';
import { SEO, breadcrumbJsonLd, organizationJsonLd } from '../components/SEO';

const About: React.FC = () => {
  const { t } = useTranslation();

  const aboutJsonLd = [
    organizationJsonLd,
    breadcrumbJsonLd([
      { name: 'Home', path: '/' },
      { name: t('footer.about'), path: '/about' },
    ]),
  ];

  return (
    <>
      <SEO
        title={`${t('legal.about.title')} | OmniScribe`}
        description={t('legal.about.metaDescription')}
        path="/about"
        jsonLd={aboutJsonLd}
      />

      <div className="min-h-screen bg-white py-16 transition-colors duration-300 dark:bg-zinc-950">
        <div className="container mx-auto max-w-4xl px-4">
          <h1 className="h-hero mb-4">{t('legal.about.heroTitle')}</h1>
          <p className="mb-12 text-lg text-slate-600 dark:text-zinc-400">{t('legal.about.heroSubtitle')}</p>

          <div className="prose prose-slate max-w-none dark:prose-invert">
            <section className="mb-10">
              <h2 className="mb-4 text-2xl font-semibold text-slate-900 dark:text-white">
                {t('legal.about.productTitle')}
              </h2>
              <div className="space-y-4 text-slate-600 dark:text-zinc-400">
                <p>{t('legal.about.productBody')}</p>
              </div>
            </section>

            <section className="mb-10">
              <h2 className="mb-4 text-2xl font-semibold text-slate-900 dark:text-white">
                {t('legal.about.companyTitle')}
              </h2>
              <div className="space-y-4 text-slate-600 dark:text-zinc-400">
                <p>{t('legal.about.companyIntro')}</p>
                <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50 p-4 not-prose dark:border-zinc-800 dark:bg-zinc-900">
                  <p className="font-medium text-slate-700 dark:text-zinc-300">
                    {t('legal.about.legalName')}
                  </p>
                  <p className="text-slate-600 dark:text-zinc-400">{t('legal.about.entityType')}</p>
                  <p className="text-slate-600 dark:text-zinc-400">{t('legal.about.founded')}</p>
                  <p className="text-slate-600 dark:text-zinc-400">{t('legal.about.fileNumber')}</p>
                </div>
              </div>
            </section>

            <section className="mb-10">
              <h2 className="mb-4 text-2xl font-semibold text-slate-900 dark:text-white">
                {t('legal.about.websitesTitle')}
              </h2>
              <div className="space-y-3 text-slate-600 dark:text-zinc-400">
                <p>
                  {t('legal.about.productSiteLabel')}:{' '}
                  <a
                    href="https://omniscribe.ai"
                    className="font-medium text-indigo-600 underline underline-offset-2 hover:text-indigo-700 dark:text-indigo-400 dark:hover:text-indigo-300"
                  >
                    omniscribe.ai
                  </a>
                </p>
                <p>
                  {t('legal.about.companySiteLabel')}:{' '}
                  <a
                    href="https://nanoultra.co"
                    className="font-medium text-indigo-600 underline underline-offset-2 hover:text-indigo-700 dark:text-indigo-400 dark:hover:text-indigo-300"
                    rel="noopener noreferrer"
                    target="_blank"
                  >
                    nanoultra.co
                  </a>
                </p>
              </div>
            </section>

            <section className="mb-10">
              <h2 className="mb-4 text-2xl font-semibold text-slate-900 dark:text-white">
                {t('legal.about.contactTitle')}
              </h2>
              <div className="space-y-4 text-slate-600 dark:text-zinc-400">
                <p>
                  {t('legal.about.contactBody')}{' '}
                  <a
                    href="mailto:support@omniscribe.ai"
                    className="font-medium text-indigo-600 underline underline-offset-2 hover:text-indigo-700 dark:text-indigo-400 dark:hover:text-indigo-300"
                  >
                    support@omniscribe.ai
                  </a>
                </p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </>
  );
};

export default About;
